"use client";

/**
 * State for a multi-step CTA: which view the dialog shows (loading, error, the steps overview, or
 * one step), the contact carried between steps, and the PostHog step events. Rendering is
 * `CtaStepSequenceDialog`'s job; deciding where to start is `useLinkedStepsCta`'s.
 *
 * A step opened from the steps run ("run" origin) advances to the next step when it completes. A
 * step opened from the overview ("overview" origin) goes back to the overview instead, so a
 * returning guest can edit one step without being walked through the rest.
 */

import { useCallback, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  EMPTY_CTA_STEP_CONTACT,
  getNextCtaStepIndex,
  type CtaStep,
  type CtaStepCompletion,
  type CtaStepContact,
  type CtaStepOrigin,
} from "../_logic/cta-steps";

/** Fired when a step of a multi-step CTA opens. */
export const CTA_STEP_STARTED_EVENT = "landing_page_cta_step_started";

/** Fired when a step of a multi-step CTA completes (fresh submit, edit, or Continue). */
export const CTA_STEP_COMPLETED_EVENT = "landing_page_cta_step_completed";

/** What the multi-step CTA dialog shows. */
export type CtaStepSequenceState =
  | { view: "loading" }
  | { view: "error" }
  | {
      view: "overview";
      contact: CtaStepContact;
      /** True right after finishing every step in a run: the overview shows "You're all set". */
      allSet: boolean;
      /** Index of the step that just completed from the overview, for its "updated" toast. */
      updatedIndex: number | null;
    }
  | { view: "step"; index: number; contact: CtaStepContact; origin: CtaStepOrigin };

interface UseCtaStepSequenceParams {
  /** The resolved steps (see `resolveCtaSteps`). */
  steps: CtaStep[];
  landingPageId: number | null;
  eventId: number;
  /** The CTA button's id, when the surface has one (card grid buttons don't). */
  buttonId?: string;
  /**
   * Whether a finished run lands on the overview. True for signed-in guests; an anonymous guest has
   * no overview, so the last step's own screen stays up, as before.
   */
  finishOnOverview: boolean;
}

interface UseCtaStepSequenceReturn {
  /** What the dialog shows, or null when the sequence isn't running. */
  state: CtaStepSequenceState | null;
  /** Opens the dialog in its loading state while the overview is fetched. */
  showLoading: () => void;
  /** Shows the "couldn't load your steps" state. */
  showError: () => void;
  /** Runs the steps from `index` (0 for a first-time guest). */
  startRun: (index?: number, contact?: CtaStepContact) => void;
  /** Shows the overview. */
  openOverview: (contact?: CtaStepContact) => void;
  /** Opens one step from the overview; completing it returns to the overview. */
  goTo: (index: number) => void;
  /** Records a step's completion and moves on (next step, or the overview). */
  complete: (index: number, completion: CtaStepCompletion) => void;
  /** Ends the sequence. Steps already submitted stay saved. */
  close: () => void;
}

/** Merges newly reported contact details over what was already carried; new values win. */
function mergeContact(previous: CtaStepContact, next: CtaStepContact): CtaStepContact {
  return {
    email: next.email ?? previous.email,
    firstName: next.firstName ?? previous.firstName,
    lastName: next.lastName ?? previous.lastName,
  };
}

/** The contact the current state carries, or an empty one. */
function contactOf(state: CtaStepSequenceState | null): CtaStepContact {
  return state && "contact" in state ? state.contact : EMPTY_CTA_STEP_CONTACT;
}

/**
 * Runs a multi-step CTA. A step moves on only when it reports completion; a step that ended its
 * form early (a `skip_to_end` answer) ends the run.
 * @param params - See {@link UseCtaStepSequenceParams}
 * @returns The current state and its actions
 * @throws Never
 */
export function useCtaStepSequence({
  steps,
  landingPageId,
  eventId,
  buttonId,
  finishOnOverview,
}: UseCtaStepSequenceParams): UseCtaStepSequenceReturn {
  // Hooks
  const posthog = usePostHog();

  // States
  const [state, setState] = useState<CtaStepSequenceState | null>(null);
  // The open step still waiting to complete, and where it was opened from; null when no step is
  // open. Only that step's report counts, so a repeat or late report (an edited hotel booking on
  // the last step, a completion after close) changes nothing and records no analytics.
  const activeStepRef = useRef<{ index: number; origin: CtaStepOrigin } | null>(null);

  // Handlers
  const captureStep = useCallback(
    (eventName: string, index: number, extra?: Record<string, unknown>) => {
      const step = steps[index];
      if (!step) return;
      posthog?.capture(eventName, {
        landing_page_id: landingPageId,
        event_id: eventId,
        button_id: buttonId ?? null,
        step_number: index + 1,
        step_count: steps.length,
        step_kind: step.kind,
        target_id: step.kind === "form" ? step.formId : step.bookingLinkId,
        ...extra,
      });
    },
    [posthog, steps, landingPageId, eventId, buttonId]
  );

  const openStep = useCallback(
    (index: number, origin: CtaStepOrigin, contact?: CtaStepContact) => {
      if (!steps[index]) return;
      activeStepRef.current = { index, origin };
      setState((previous) => ({ view: "step", index, contact: contact ?? contactOf(previous), origin }));
      captureStep(CTA_STEP_STARTED_EVENT, index, { origin });
    },
    [steps, captureStep]
  );

  const showLoading = useCallback(() => {
    activeStepRef.current = null;
    setState({ view: "loading" });
  }, []);

  const showError = useCallback(() => {
    activeStepRef.current = null;
    setState({ view: "error" });
  }, []);

  const startRun = useCallback(
    (index = 0, contact: CtaStepContact = EMPTY_CTA_STEP_CONTACT) => openStep(index, "run", contact),
    [openStep]
  );

  const openOverview = useCallback((contact?: CtaStepContact) => {
    activeStepRef.current = null;
    setState((previous) => ({
      view: "overview",
      contact: contact ?? contactOf(previous),
      allSet: false,
      updatedIndex: null,
    }));
  }, []);

  const goTo = useCallback((index: number) => openStep(index, "overview"), [openStep]);

  const complete = useCallback(
    (index: number, completion: CtaStepCompletion) => {
      const active = activeStepRef.current;
      if (!active || active.index !== index) return;
      captureStep(CTA_STEP_COMPLETED_EVENT, index, { ended_early: completion.endedEarly, origin: active.origin });

      const toOverview = (allSet: boolean, updatedIndex: number | null) => {
        activeStepRef.current = null;
        setState((previous) => ({
          view: "overview",
          contact: mergeContact(contactOf(previous), completion.contact),
          allSet,
          updatedIndex,
        }));
      };

      if (active.origin === "overview") {
        toOverview(false, index);
        return;
      }

      const nextIndex = getNextCtaStepIndex(index, steps.length, completion.endedEarly);
      if (nextIndex === null) {
        // A run that ends: signed-in guests land on the overview (all set, or with the decline
        // prompt when they ended early); anonymous guests keep the last step's own screen.
        if (finishOnOverview) toOverview(!completion.endedEarly, null);
        else activeStepRef.current = null;
        return;
      }

      activeStepRef.current = { index: nextIndex, origin: "run" };
      setState((previous) => ({
        view: "step",
        index: nextIndex,
        contact: mergeContact(contactOf(previous), completion.contact),
        origin: "run",
      }));
      captureStep(CTA_STEP_STARTED_EVENT, nextIndex, { origin: "run" });
    },
    [steps.length, captureStep, finishOnOverview]
  );

  const close = useCallback(() => {
    activeStepRef.current = null;
    setState(null);
  }, []);

  return { state, showLoading, showError, startRun, openOverview, goTo, complete, close };
}
